<template>
  <div id="share">
    <div class="ContentContainer">
      <div
        id="loading"
        v-show="sortedAssets.length === 0 && hadErrors === false"
      >
        {{ $t("share_page.just_second") }}
      </div>

      <div id="error" v-show="hadErrors">
        <div class="row">
          <div class="col-12 col-sm-6">
            <h3 class="heading-lined">
              {{ $t("share_page.porfolio_not_found") }}
            </h3>
            <p v-html="$t('share_page.p_1')"></p>

            <router-link to="/" class="btn btn-secondary btn-lg mt-4">{{
              $t("share_page.create_own_free")
            }}</router-link>
          </div>
          <div class="col-12 col-sm-6">
            <img class="img-fluid" src="./../../assets/demo-portfolio.png" />
          </div>
        </div>
      </div>

      <div
        id="portfolio"
        v-show="sortedAssets.length > 0 && hadErrors === false"
      >
        <div class="share-intro">
          <p class="mb-0" v-html="$t('share_page.p_2')"></p>
        </div>

        <div class="row">
          <div class="col">
            <div class="Portfolio">
              <h3 class="heading-lined">{{ $t("share_page.porfolio") }}</h3>
              <div class="Portfolio__Chart">
                <ul class="Portfolio__Assets">
                  <li
                    class="Portfolio__Asset"
                    v-for="(asset, index) in sortedAssets"
                    v-bind:key="index"
                  >
                    <span
                      class="Portfolio__Asset-Color"
                      :style="{ backgroundColor: getChartColor(index) }"
                    ></span>
                    {{ asset.name }}
                    <em>{{ formatNumber(asset.share) }}%</em>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-sm-6">
            <h3 class="heading-lined">{{ $t("share_page.profit_losed") }}</h3>
            <table class="ProfitLosses">
              <colgroup>
                <col width="20%" />
                <col width="40%" />
              </colgroup>
              <tr class="ProfitLosses__Row">
                <td class="ProfitLosses__Range">
                  {{ $t("global.last_hour") }}
                </td>
                <td
                  :class="[
                    'ProfitLosses__Amount',
                    portfolio.change.hour < 0
                      ? 'ProfitLosses__Amount--Negative'
                      : '',
                  ]"
                >
                  {{ portfolio.change.hour.toFixed(2) }}%
                </td>
              </tr>
              <tr class="ProfitLosses__Row">
                <td class="ProfitLosses__Range">
                  {{ $t("global.last_24_hour") }}
                </td>
                <td
                  :class="[
                    'ProfitLosses__Amount',
                    portfolio.change.day < 0
                      ? 'ProfitLosses__Amount--Negative'
                      : '',
                  ]"
                >
                  {{ portfolio.change.day.toFixed(2) }}%
                </td>
              </tr>
              <tr class="ProfitLosses__Row">
                <td class="ProfitLosses__Range">
                  {{ $t("global.last_7_days") }}
                </td>
                <td
                  :class="[
                    'ProfitLosses__Amount',
                    portfolio.change.week < 0
                      ? 'ProfitLosses__Amount--Negative'
                      : '',
                  ]"
                >
                  {{ portfolio.change.week.toFixed(2) }}%
                </td>
              </tr>
            </table>

            <div v-if="!isMobile">
              <div class="card Meta" v-if="portfolio.message.length > 0">
                <div class="card-block p-3">{{ portfolio.message }}</div>
              </div>

              <p class="last-update" v-bind:title="creationDateTime">
                {{ $t("share_page.created") }} {{ relativeCreationTime }}
              </p>
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="Chart">
              <canvas ref="chart"></canvas>
            </div>

            <div class="Chart__Tooltip" ref="tooltip">
              <table>
                <tr>
                  <td colspan="2" class="Chart__Tooltip__Header">
                    {{ tooltipAsset.name }}
                    <em>({{ tooltipAsset.symbol }})</em>
                  </td>
                </tr>
                <tr>
                  <td>{{ $t("global.share") }}</td>
                  <td>{{ formatNumber(tooltipAsset.share) }}%</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isMobile">
        <div class="card Meta" v-if="portfolio.message.length > 0">
          <div class="card-block">{{ portfolio.message }}</div>
        </div>

        <p class="last-update" v-bind:title="creationDateTime">
          {{ $t("share_page.created") }} {{ relativeCreationTime }}
        </p>
      </div>
      <!-- <div class="btn-container" v-if="this.$store.state.user.isLoggedIn">
        <button v-if="!portfolioExist" @click="portfolioDialog" class="btn btn-secondary">Save</button>
        <button v-if="portfolioExist" @click="portfolioDialog" class="btn btn-secondary">Delete</button>
      </div> -->
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ago from "s-ago";
import Chart from "chart.js";
import Options from "@/options";
import MobileMixin from "@/mixins/mobile";
import NumbersMixin from "@/mixins/numbers";

export default {
  name: "Share",
  mixins: [MobileMixin, NumbersMixin],
  data() {
    return {
      hadErrors: false,
      portfolio: {
        change: {
          hour: 0,
          day: 0,
          week: 0,
        },
        message: "",
      },
      sortedAssets: [],
      tooltipAsset: [],
    };
  },
  computed: {
    relativeCreationTime() {
      return ago(new Date(this.portfolio.created * 1000));
    },
    creationDateTime() {
      return new Date(this.portfolio.created * 1000).toLocaleString();
    },
    chartData() {
      return this.sortedAssets.map((asset) => asset.share);
    },
    chartLabels() {
      return this.sortedAssets.map((asset) => asset.name);
    },
    chartColors() {
      return Options.chartColors.map((color, key) => {
        return this.sortedAssets[key] &&
          this.sortedAssets[key].color &&
          /^\#[A-F0-9]+$/i.test(this.sortedAssets[key].color)
          ? this.sortedAssets[key].color
          : color;
      });
    },
  },
  methods: {
    getChartColor(index) {
      return this.chartColors[index];
    },
    renderChart() {
      this.chart = new Chart(this.$refs.chart, {
        type: "pie",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              label: "Assets",
              data: this.chartData,
              backgroundColor: this.chartColors,
              borderColor: "#fff",
              borderWidth: 2,
            },
          ],
        },
        options: {
          aspectRatio: 1,
          showTooltips: false,
          legend: { display: false },
          animation: { duration: 0 },
          tooltips: {
            enabled: false,
            custom: (tooltipModel) => {
              // Tooltip Element
              const tooltipEl = this.$refs.tooltip;
              // Hide if no tooltip
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }
              this.tooltipAsset =
                this.sortedAssets[tooltipModel.dataPoints[0].index];
              // Display, position, and set styles for font
              tooltipEl.style.opacity = 1;
              tooltipEl.style.left = `${tooltipModel.caretX}px`;
              tooltipEl.style.top = `${tooltipModel.caretY}px`;
            },
          },
        },
      });
    },
    ...mapActions({
      getSharedData: "portfolio/getSharedData",
    }),
  },
  async mounted() {
    try {
      this.portfolio = await this.getSharedData(this.$route.params.hash);
      this.sortedAssets = this.portfolio.assets.sort(
        (a, b) => b.share - a.share
      );

      this.renderChart();
    } catch (error) {
      this.hadErrors = true;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~@/scss/variables";
@import "~@/scss/mixins";

.btn-container {
  display: flex;
  justify-content: space-around;
  width: 100%;
}
.share-intro {
  background: #eee;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 35px;

  strong {
    font-size: 20px;
    display: block;
    margin-bottom: 3px;
  }
}

.Portfolio {
  margin-bottom: 50px;
}

.Portfolio__Assets {
  list-style: none;
  padding: 0;
  line-height: 2;
}

.Portfolio__Asset {
  display: inline-block;
  margin: 0 8px;
  em {
    font-style: normal;
    opacity: 0.5;
    font-weight: 300;
  }
}

.Portfolio__Asset-Color {
  display: inline-block;
  height: 16px;
  width: 16px;
  vertical-align: middle;
  overflow: hidden;
  border-radius: 50%;
  position: relative;
  top: -1px;
  margin-right: 5px;
}

.ProfitLosses {
  margin: 0;
  width: 100%;
  font-size: 18px;
  font-weight: 300;
}

.ProfitLosses__Row {
  border-bottom: 1px solid #ebebeb;

  &:last-child {
    border-bottom: 0;
  }
}

.ProfitLosses__Range {
  font-weight: 700;
}

.ProfitLosses__Amount,
.ProfitLosses__Range {
  padding: 12px 0;
  white-space: nowrap;
}

.ProfitLosses__Amount {
  color: $color-tertiary;
  text-align: right;

  &--Negative {
    color: $color-primary;
  }
}

.Meta {
  margin: 50px 0 0;
}

.last-update {
  color: #8da3ad;
  margin: 50px 0 0;

  @include breakpoint(small) {
    margin-bottom: 0;
  }
}

.Chart {
  max-width: 80%;
  margin: 0 auto;
  position: relative;

  @include breakpoint(small) {
    max-width: 90%;
    margin: 0 0 0 auto;
  }
}

.Chart__Tooltip {
  opacity: 0;
  display: block;
  position: absolute;
  background: rgba(#000, 0.8);
  color: #fff;
  transition: all 0.2s ease;
  pointer-events: none;
  white-space: nowrap;

  font-size: 15px;
  padding: 12px 20px;
  border-radius: 15px;

  @include breakpoint-manual(max-width, $break-extra-large - 1px) {
    transform: translate(-50%, 0%);
  }

  .Chart__Tooltip__Header {
    font-weight: bold !important;
    font-size: 16px;
  }

  td:first-child {
    padding-right: 8px;
    width: 80px;
    font-weight: bold;
  }
}
</style>
